import { useMemo } from 'react'
import toast from 'react-hot-toast'
import { useOutletContext } from 'react-router'
import { MinusCircleIcon } from '@/components/ui/icons'
import { Spinner } from '@/components/ui/spinner'
import { useDeallocateImages } from '@/features/photoset/queries/use-deallocate-images'
import { useReallocateImages } from '@/features/photoset/queries/use-reallocate-images'
import { usePhotosetStore } from '@/features/photoset/store/store'
import type {
	Image,
	OnAllocateAction,
	PhotosetSpace,
	PhotosetURLParams
} from '@/features/photoset/types'
import { generateToastAllocationId } from '@/features/photoset/utils'
import { errorHandler } from '@/helpers/utils'
import { UndoMessage } from './undo-message'

interface DeallocateImageButtonProps {
	image: Image
	photosetSpaceId: PhotosetSpace['id']
	keepSelected: boolean
	trackInHistory: boolean
	showToast: boolean
}

export const DeallocateImageButton = ({
	image,
	photosetSpaceId,
	keepSelected,
	trackInHistory,
	showToast
}: DeallocateImageButtonProps) => {
	const { urlParams } = useOutletContext<{ urlParams: PhotosetURLParams }>()
	const allocateImages = usePhotosetStore((state) => state.allocateImages)
	const deallocateImages = usePhotosetStore((store) => store.deallocateImages)
	const photosetSpace = usePhotosetStore((store) => store.photosetSpaces)
	const deallocateImagesMutation = useDeallocateImages()
	const reallocateImagesMutation = useReallocateImages()

	const photoset = useMemo(
		() =>
			[...photosetSpace.values()].find(
				(space) => space.id === photosetSpaceId
			),
		[photosetSpace, photosetSpaceId]
	)

	const onUndo = async (imagesIds: Image['id'][]) => {
		try {
			const toastId = generateToastAllocationId(
				'deallocation',
				photosetSpaceId,
				imagesIds
			)
			await reallocateImagesMutation.mutateAsync({
				urlParams: { photosetId: urlParams.id, photosetSpaceId },
				payload: imagesIds
			})
			allocateImages({
				imagesIds,
				photosetSpaceId,
				keepSelected: false,
				trackInHistory: false
			})
			toast.dismiss(toastId)
		} catch (error) {
			errorHandler(error)
		}
	}

	const onDeallocate = async ({
		photosetSpaceId,
		imagesIds
	}: OnAllocateAction) => {
		try {
			await deallocateImagesMutation.mutateAsync({
				urlParams: {
					photosetId: urlParams.id,
					photosetSpaceId
				},
				payload: imagesIds
			})

			deallocateImages({
				imagesIds,
				photosetSpaceId,
				keepSelected,
				trackInHistory
			})

			if (showToast) {
				const toastId = generateToastAllocationId(
					'deallocation',
					photosetSpaceId,
					imagesIds
				)

				toast.success(
					<UndoMessage
						onUndo={() => onUndo(imagesIds)}
						operationType={'deallocation'}
						spaceKeyName={photoset?.space.key_name}
					/>,
					{ duration: 8000, id: toastId }
				)
			}
		} catch (error) {
			errorHandler(error)
		}
	}

	return (
		<button
			className={`relative top-0 left-0 flex h-full w-full cursor-pointer items-center justify-center bg-neutral-900/30 text-base text-neutral-white transition-opacity duration-200 ease-in-out ${
				deallocateImagesMutation.isPending
					? ''
					: 'hover:opacity-100 [&:not(:hover)]:opacity-0'
			}`}
			disabled={deallocateImagesMutation.isPending}
			onClick={() =>
				onDeallocate({
					imagesIds: [image.id],
					photosetSpaceId
				})
			}
			type='button'
		>
			{deallocateImagesMutation.isPending ? (
				<Spinner size={24} />
			) : (
				<MinusCircleIcon />
			)}
		</button>
	)
}
